/**
 * Janela principal do chat - Versão Premium
 * Responsivo com mobile fullscreen, animações e dark mode
 */

import React, { useRef, useEffect, useState } from 'react';
import type { ChatMessage, InteractiveButton } from '../types';
import { Header } from './Header';
import { MessageBubble } from './MessageBubble';
import { InputArea } from './InputArea';
import { PreChatForm } from './PreChatForm';
import { TypingIndicator } from './TypingIndicator';
// Componentes de screen share WebRTC
import { ScreenShareRequest } from './ScreenShareRequest';
import { ScreenShareIndicator } from './ScreenShareIndicator';
import type { useClientScreenShare } from '@/hooks/webrtc';

interface ChatWindowProps {
  isOpen: boolean;
  messages: ChatMessage[];
  onSendMessage: (message: string) => void;
  onSendFile?: (file: File) => void;
  onRetryMessage: (messageId: string) => void;
  onClose: () => void;
  primaryColor: string;
  position: 'bottom-right' | 'bottom-left';
  greetingMessage: string;
  requireName: boolean;
  requireEmail: boolean;
  requirePhone: boolean;
  showBranding: boolean;
  isSending: boolean;
  isConnected: boolean;
  showPreChatForm: boolean;
  onPreChatSubmit: (name: string, email: string, phone: string) => void;
  isAgentTyping?: boolean;
  companyName?: string;
  // Callback para clique em botões interativos do Flow Engine
  onButtonClick?: (button: InteractiveButton) => void;
  // Habilita upload de arquivos (5.4)
  enableFileUpload?: boolean;
  // Paginação de mensagens antigas
  hasMoreMessages?: boolean;
  onLoadMoreMessages?: () => Promise<void>;
  isLoadingHistory?: boolean;
  // Erro ao carregar histórico (para exibir banner)
  historyError?: string | null;
  // Callback para tentar novamente carregar histórico
  onRetryHistory?: () => void;
  // Novas props para avatar dinâmico no header
  headerAvatarMode?: 'logo' | 'agent';
  headerLogoUrl?: string | null;
  // ===== WEBRTC SCREEN SHARE =====
  screenShareState?: ReturnType<typeof useClientScreenShare>;
}

export const ChatWindow: React.FC<ChatWindowProps> = ({
  isOpen,
  messages,
  onSendMessage,
  onSendFile,
  onRetryMessage,
  onClose,
  primaryColor,
  position,
  greetingMessage,
  requireName,
  requireEmail,
  requirePhone,
  showBranding,
  isSending,
  isConnected,
  showPreChatForm,
  onPreChatSubmit,
  isAgentTyping = false,
  companyName = 'Suporte',
  onButtonClick,
  enableFileUpload = false,
  hasMoreMessages = false,
  onLoadMoreMessages,
  isLoadingHistory = false,
  historyError = null,
  onRetryHistory,
  // Novas props para avatar dinâmico
  headerAvatarMode = 'agent',
  headerLogoUrl,
  // ===== WEBRTC SCREEN SHARE =====
  screenShareState,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  
  // Refs para controle de scroll inteligente
  const isFirstOpen = useRef(true);
  const previousScrollHeight = useRef<number>(0);
  const previousMessagesCount = useRef<number>(0);

  // Detecta mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 480);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Scroll inteligente: instantâneo na primeira abertura, suave para novas mensagens
  useEffect(() => {
    if (!messagesEndRef.current || !isOpen) return;
    
    // Verifica se estamos carregando mensagens antigas (loadMore)
    // Nesse caso, preservamos a posição do scroll
    if (previousScrollHeight.current > 0 && messagesContainerRef.current) {
      const newScrollHeight = messagesContainerRef.current.scrollHeight;
      const scrollDiff = newScrollHeight - previousScrollHeight.current;
      messagesContainerRef.current.scrollTop += scrollDiff;
      previousScrollHeight.current = 0;
      previousMessagesCount.current = messages.length;
      return;
    }
    
    // Na primeira abertura, vai direto sem animação
    if (isFirstOpen.current) {
      isFirstOpen.current = false;
      previousMessagesCount.current = messages.length;
      messagesEndRef.current.scrollIntoView({ behavior: 'instant' as ScrollBehavior });
      return;
    }
    
    // Novas mensagens recebidas em tempo real: scroll suave
    if (messages.length > previousMessagesCount.current) {
      previousMessagesCount.current = messages.length;
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isAgentTyping, isOpen]);

  // Handler para carregar mais mensagens (preserva posição do scroll)
  const handleLoadMore = async () => {
    if (messagesContainerRef.current && onLoadMoreMessages) {
      previousScrollHeight.current = messagesContainerRef.current.scrollHeight;
      await onLoadMoreMessages();
    }
  };

  // Handle close com animação
  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 200);
  };

  if (!isOpen && !isClosing) return null;

  // Estilos do container (responsivo)
  const containerStyles: React.CSSProperties = isMobile ? {
    position: 'fixed',
    inset: 0,
    backgroundColor: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 9998,
    animation: isClosing ? 'mobileSlideOut 0.2s ease-in forwards' : 'mobileSlideIn 0.3s ease-out',
  } : {
    position: 'fixed',
    bottom: '90px',
    [position === 'bottom-right' ? 'right' : 'left']: '20px',
    width: '380px',
    maxWidth: 'calc(100vw - 40px)',
    height: '550px',
    maxHeight: 'calc(100vh - 120px)',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 12px 48px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(0, 0, 0, 0.08)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    zIndex: 9998,
    animation: isClosing ? 'windowSlideOut 0.2s ease-in forwards' : 'windowSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  };

  // Estilos da área de mensagens
  const messagesAreaStyles: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    padding: '12px 0',
    backgroundColor: '#fafafa',
    scrollBehavior: 'smooth',
  };

  // Estilos do branding
  const brandingStyles: React.CSSProperties = {
    padding: '10px',
    textAlign: 'center',
    fontSize: '11px',
    color: '#9ca3af',
    backgroundColor: '#f9fafb',
    borderTop: '1px solid #e5e7eb',
  };

  // Mensagem de boas-vindas vazia
  const EmptyState = () => (
    <div style={{
      padding: '32px 24px',
      textAlign: 'center',
      color: '#6b7280',
    }}>
      <div style={{
        width: '64px',
        height: '64px',
        borderRadius: '50%',
        backgroundColor: `${primaryColor}15`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 16px',
      }}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={primaryColor} strokeWidth="1.5">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      </div>
      <p style={{ margin: 0, fontSize: '15px', lineHeight: '1.6' }}>
        {greetingMessage}
      </p>
    </div>
  );

  return (
    <>
      <style>
        {`
          @keyframes windowSlideIn {
            0% {
              opacity: 0;
              transform: translateY(20px) scale(0.95);
            }
            100% {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
          @keyframes windowSlideOut {
            0% {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
            100% {
              opacity: 0;
              transform: translateY(20px) scale(0.95);
            }
          }
          @keyframes mobileSlideIn {
            0% {
              opacity: 0;
              transform: translateY(100%);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes mobileSlideOut {
            0% {
              opacity: 1;
              transform: translateY(0);
            }
            100% {
              opacity: 0;
              transform: translateY(100%);
            }
          }
          .uaizychat-messages-area::-webkit-scrollbar {
            width: 6px;
          }
          .uaizychat-messages-area::-webkit-scrollbar-track {
            background: transparent;
          }
          .uaizychat-messages-area::-webkit-scrollbar-thumb {
            background: #d1d5db;
            border-radius: 3px;
          }
          .uaizychat-messages-area::-webkit-scrollbar-thumb:hover {
            background: #9ca3af;
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>

      <div 
        style={containerStyles}
        role="dialog"
        aria-label="Chat de suporte"
        aria-modal="true"
      >
        {/* Header */}
        <Header
          title={companyName}
          isConnected={isConnected}
          isTyping={isAgentTyping}
          primaryColor={primaryColor}
          onClose={handleClose}
          headerAvatarMode={headerAvatarMode}
          headerLogoUrl={headerLogoUrl}
        />

        {/* ===== WEBRTC SCREEN SHARE - INDICADOR ATIVO ===== */}
        {/* Mostra badge quando compartilhamento está ativo */}
        {screenShareState?.sessionState === 'connected' && (
          <ScreenShareIndicator
            primaryColor={primaryColor}
            onStop={screenShareState.endSession}
          />
        )}

        {/* ===== WEBRTC SCREEN SHARE - MODAL DE SOLICITAÇÃO ===== */}
        {/* Modal de consentimento quando agente solicita visualização */}
        {screenShareState?.sessionState === 'pending' && screenShareState?.pendingRequest && (
          <ScreenShareRequest
            request={screenShareState.pendingRequest}
            primaryColor={primaryColor}
            onAccept={screenShareState.acceptRequest}
            onReject={screenShareState.rejectRequest}
          />
        )}

        {/* Conteúdo */}
        {showPreChatForm ? (
          <PreChatForm
            onSubmit={onPreChatSubmit}
            primaryColor={primaryColor}
            requireName={requireName}
            requireEmail={requireEmail}
            requirePhone={requirePhone}
            greetingMessage={greetingMessage}
          />
        ) : (
          <>
            {/* Área de mensagens */}
            <div 
              ref={messagesContainerRef}
              style={messagesAreaStyles}
              className="uaizychat-messages-area"
              role="log"
              aria-live="polite"
              aria-label="Mensagens do chat"
            >
              {/* Banner de erro ao carregar histórico */}
              {historyError && (
                <div
                  style={{
                    margin: '8px 12px',
                    padding: '10px 12px',
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    fontSize: '13px',
                    color: '#991b1b',
                  }}
                >
                  <span>Não foi possível carregar mensagens anteriores.</span>
                  {onRetryHistory && (
                    <button
                      onClick={onRetryHistory}
                      style={{
                        padding: '4px 10px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #fecaca',
                        borderRadius: '6px',
                        color: '#991b1b',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Tentar novamente
                    </button>
                  )}
                </div>
              )}

              {/* Botão para carregar mensagens anteriores */}
              {hasMoreMessages && (
                <button
                  onClick={handleLoadMore}
                  disabled={isLoadingHistory}
                  style={{
                    width: 'calc(100% - 24px)',
                    padding: '10px 12px',
                    margin: '8px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    backgroundColor: '#ffffff',
                    color: '#6b7280',
                    fontSize: '13px',
                    cursor: isLoadingHistory ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseOver={(e) => {
                    if (!isLoadingHistory) {
                      e.currentTarget.style.backgroundColor = '#f3f4f6';
                      e.currentTarget.style.borderColor = primaryColor;
                    }
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#ffffff';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                >
                  {isLoadingHistory ? (
                    <>
                      <span style={{ 
                        display: 'inline-block', 
                        animation: 'spin 1s linear infinite',
                      }}>⟳</span>
                      Carregando...
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: '14px' }}>↑</span>
                      Carregar mensagens anteriores
                    </>
                  )}
                </button>
              )}
              
              {messages.length === 0 ? (
                <EmptyState />
              ) : (
                <div role="list">
                  {messages.map((message, index) => (
                    <MessageBubble
                      key={message.id}
                      message={message}
                      primaryColor={primaryColor}
                      isLast={index === messages.length - 1}
                      onRetry={
                        message.status === 'failed'
                          ? () => onRetryMessage(message.id)
                          : undefined
                      }
                      onButtonClick={onButtonClick}
                    />
                  ))}
                </div>
              )}
              
              {/* Indicador de digitação */}
              {isAgentTyping && (
                <TypingIndicator primaryColor={primaryColor} />
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input com suporte a upload (5.4) */}
            <InputArea
              onSend={onSendMessage}
              onSendFile={onSendFile}
              primaryColor={primaryColor}
              disabled={isSending}
              enableFileUpload={enableFileUpload}
            />
          </>
        )}

        {/* Branding */}
        {showBranding && (
          <div style={brandingStyles}>
            <span>Powered by </span>
            <a 
              href="https://uaizychat.com.br" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ 
                color: primaryColor, 
                fontWeight: '600',
                textDecoration: 'none',
              }}
            >
              UaizyChat
            </a>
          </div>
        )}
      </div>
    </>
  );
};
